import React from 'react';
import { MapPin, Calendar, Camera } from 'lucide-react';

const Ngorongoro = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white text-[#2f4f2f] py-4 px-6 border-b border-[#9acd32]/20">
        <h1 className="text-2xl font-bold">NGORONGORO CONSERVATION AREA </h1>
      </header>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Title */}
        <h2 className="text-3xl font-bold text-[#2f4f2f] mb-6">
          Ngorongoro Conservation Area - Ngorongoro Crater
        </h2>

        {/* Introduction */}
        <div className="mb-8">
          <p className="text-gray-700 leading-relaxed mb-4">
            The <strong>Ngorongoro Conservation Area</strong> is a protected conservation area and World Heritage Site in northern Tanzania. It encompasses 8,292 square kilometers (3,202 sq mi). The area is named after <strong>Ngorongoro Crater</strong>, a large volcanic caldera within the area. The conservation area is administered by the Ngorongoro Conservation Area Authority, an arm of the Tanzanian government, and its boundaries follow the boundary of the Ngorongoro District in Arusha Region. The western portion of the park abuts the Serengeti National Park, and the area comprises multiple usage zones.
          </p>
          
          <p className="text-gray-700 leading-relaxed mb-4">
            Lake Natron area is located in the Ngorongoro Crater and is one of the world's most unforgettable cultures. The conservation area also hosts significant archaeological sites, including <strong>Olduvai Gorge</strong>, sometimes referred to as the cradle of mankind. The northern and western rims rise 2,000 meters above the crater floor.
          </p>
          
          <p className="text-gray-700 leading-relaxed mb-4">
            The primary volcanic structures, including Ngorongoro Crater and the volcanoes Olmoti and Empakaai, formed between 20 million years ago. Embedded Crater is especially notable for containing lake, which intermittently half its eastern face.
          </p>
          
          <p className="text-gray-700 leading-relaxed">
            <strong>Ngorongoro Conservation Area</strong> supports the world's largest herds of ungulates, including <strong>wildebeest</strong>, plains zebras, and gazelles. The conservation area is also home to the densest population of <strong>lions</strong> in the world. Black rhinoceros, warthogs, hippopotami, elephants, flamingos, and the endangered black rhinoceros and African hunting dog. Archaeological finds more than what had been collected in the area are fossils, pottery, stone circles, and human remains, ancient cave springs, and bronze, and Maasai settlements remain to the site of the oldest found there, along with fossilized hominids, carved cave-rings, and bronze and Maasai settlements in the area.
          </p>
        </div>

        {/* Designation Info */}
        <div className="mb-8">
          <p className="text-gray-700 leading-relaxed">
            Initially part of the <strong>Serengeti National Park</strong> in 1951, the <strong>Ngorongoro Conservation Area</strong> was designated as a separate entity in 1959 and as a UNESCO <strong>World Heritage Site</strong> in 1979. Through partnerships established with neighboring communities. 23 sites within Masaai are permitted to graze their livestock within the area. Concerns in the site 20th century included ecosystem damage from overgrazing but have been addressed and the area is currently conserved. Recent developments are conserving them in the area of cultural lands and World Heritage status.
          </p>
        </div>

        {/* Best Time to Visit Section */}
        <div className="mb-8 p-6 border border-[#9acd32]/20 rounded-lg">
          <h3 className="text-2xl font-bold text-[#2f4f2f] mb-4 flex items-center">
            <Calendar className="mr-2" size={24} />
            Best Time to Visit the Ngorongoro Conservation Area
          </h3>
          <p className="text-gray-700 leading-relaxed mb-4">
            The <strong>Ngorongoro Conservation Area</strong> experiences mild temperatures year round, with short rains occurring in October and November, and longer rains from March to May. Daytime temperatures in the crater are generally moderate while nighttime temperatures can be chilly. The dry months of July and August provide the most favorable conditions for game viewing in the Ngorongoro Crater.
          </p>
          <div className="mt-3 text-sm text-[#2f4f2f] font-medium">
            Ready to Plan a Tanzania Safari? Contact Our Travel Advisor Now (Call/WhatsApp)
            <br />
            <a href="tel:+255753182737" className="text-[#9acd32] hover:text-[#228b22] transition-colors">+255 753 182 737</a>
          </div>
        </div>

        {/* How to Get There Section */}
        <div className="mb-8">
          <h3 className="text-2xl font-bold text-[#2f4f2f] mb-4 flex items-center">
            <MapPin className="mr-2" size={24} />
            How to Get There
          </h3>
          <div className="mb-4">
            <h4 className="text-lg font-semibold text-[#2f4f2f] mb-2">By Road</h4>
            <p className="text-gray-700 leading-relaxed">
              Arusha, the closest town, is about 190 miles (180 km) away, a two-hour drive passing through Karatu. Visitors typically enter the Ngorongoro Conservation Area via the Lodare Gate near Karatu. Safari travelers in the Serengeti can access the Ngorongoro Conservation Area through the Naabi Hill Gate, driving on gravel roads and keeping a lookout for wildlife along the way.
            </p>
          </div>
          <div>
            <h4 className="text-lg font-semibold text-[#2f4f2f] mb-2">By Air</h4>
            <p className="text-gray-700 leading-relaxed">
              The nearest airports to the Ngorongoro Conservation Area are Arusha International Airport and Kilimanjaro International Airport. From which guests would need to transfer by road. There are companies that offer chartered flights to an airstrip situated at the rim of the Ngorongoro Crater.
            </p>
          </div>
        </div>

        {/* Safari Packages Section */}
        <div className="mb-8">
          <h3 className="text-2xl font-bold text-[#2f4f2f] mb-6 flex items-center">
            <Camera className="mr-2" size={24} />
            Our Top Tanzania Safari Packages to Ngorongoro Crater
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Safari Package 1 */}
            <div className="bg-white rounded-lg shadow-lg overflow-hidden">
              <img 
                src="/21.jpg" 
                alt="Leopard safari"
                className="w-full h-48 object-cover"
              />
              <div className="p-4">
                <div className="bg-[#9acd32] text-[#2f4f2f] px-3 py-1 rounded text-sm inline-block mb-2">
                  5 Days Tanzania Luxury Safari
                </div>
                <p className="text-gray-700 text-sm font-medium">
                  Experience luxury wildlife viewing with expert guides
                </p>
              </div>
            </div>

            {/* Safari Package 2 */}
            <div className="bg-white rounded-lg shadow-lg overflow-hidden">
              <img 
                src="/63.jpg" 
                alt="Lions safari"
                className="w-full h-48 object-cover"
              />
              <div className="p-4">
                <div className="bg-[#9acd32] text-[#2f4f2f] px-3 py-1 rounded text-sm inline-block mb-2">
                  6 Days Best of Tanzania Safari
                </div>
                <p className="text-gray-700 text-sm font-medium">
                  Discover the best wildlife destinations in Tanzania
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Ngorongoro;